import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DataProtection, Header } from '@aptsi-types';
import { DataAssetMappingService } from '../shared/services/data-asset-mapping.service';
import { MatDialog } from '@angular/material/dialog';
import { ViewMappingComponent } from './view-mapping/view-mapping.component';
import { Router } from '@angular/router';

type DataProtectionRecord = DataProtection & Record<string, unknown>;

type MappingPaginationMeta = Pick<
  DataProtection,
  'totalDataAssets' | 'totalPages' | 'currentPage'
>;

interface NormalizedMappingResponse {
  rows: DataProtection[];
  metadata?: MappingPaginationMeta;
}

@Component({
  selector: 'app-data-asset-mapping',
  templateUrl: './data-asset-mapping.component.html',
  styleUrls: ['./data-asset-mapping.component.scss'],
})
export class DataAssetMappingComponent implements OnInit {
  readonly itemsPerPage = 10;

  currentPage = 1;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  issearch = false;

  searchText = '';
  filterByHeader = 'all';

  filteredData: DataProtection[] = [];
  searchedData: DataProtection[] = [];

  private cachedSource: DataProtection[] | null = null;
  private cachedTableData: DataProtection[] = [];

  /** Ensures only the latest page/search request can update component state. */
  private requestSequence = 0;

  readonly headers: Header[] = [
    {
      name: 'Data Asset ID',
      property: 'dataAssetId',
      showInDropdown: true,
      description: 'Unique identifier of the data asset',
    },
    {
      name: 'Data Asset Name',
      property: 'dataAssetName',
      showInDropdown: true,
      description: 'Name of the data asset',
    },
    {
      name: 'Description',
      property: 'dataAssetDescription',
      showInDropdown: true,
      description: 'Description of the data asset',
    },
    {
      name: 'Mapping Status',
      property: 'mappingStatus',
      showInDropdown: true,
      description: 'Whether the data asset has been mapped',
    },
  ];


readonly buttonConfig = (row: any): Record<string, boolean> => {
  const status = String(
    row?.mappingStatus ??
    row?.mapping_status ??
    row?.[''] ??
    ''
  )
    .trim()
    .toUpperCase();

  const isMapped = status === 'MAPPED';

  return {
    map: !isMapped,
    view: isMapped || status === '',
    update: isMapped,
  };
};

constructor(
  private readonly dataAssetMappingService: DataAssetMappingService,
  private readonly changeDetector: ChangeDetectorRef,
  private readonly dialog: MatDialog,
  private readonly router: Router
) {}

  ngOnInit(): void {
    void this.fetchDataAssetsWithMappingStatus();
  }

  get displayedData(): DataProtection[] {
    return this.issearch ? this.searchedData : this.filteredData;
  }

  get currentTotalItems(): number {
    return this.issearch ? this.totalItemsSearch : this.totalItemsNormal;
  }

  get currentTotalPages(): number {
    return this.issearch ? this.totalPagesSearch : this.totalPagesNormal;
  }

  /**
   * Normalize the field used by app-main-table and retain a stable reference
   * until the visible source rows change.
   */
  get tableData(): DataProtection[] {
    const source = this.displayedData;
    if (source === this.cachedSource) {
      return this.cachedTableData;
    }

    this.cachedTableData = source.map((asset) => {
      const row: Record<string, unknown> = {
        ...(asset as DataProtectionRecord),
      };

      for (const header of this.headers) {
        const value = row[header.property];
        row[header.property] =
          value === null || value === undefined || String(value).trim() === ''
            ? '—'
            : value;
      }

      // Keep the normalized status value available to the shared table so its
      // mappingStatus column can apply mapped/unmapped text colors.
      row['mappingStatus'] = this.normalizeMappingStatus(row['mappingStatus']);
      return row as unknown as DataProtection;
    });

    this.cachedSource = source;
    return this.cachedTableData;
  }

  async fetchDataAssetsWithMappingStatus(): Promise<void> {
    const requestId = ++this.requestSequence;
    this.isLoading = true;
    this.isSearching = false;
    this.loadError = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await this.dataAssetMappingService
        .FetchDataAssetsWithMappingStatus(this.currentPage - 1)
        .toPromise();

      if (requestId !== this.requestSequence) {
        return;
      }

      const normalized = this.normalizeResponse(apiResponse);
      this.filteredData = normalized.rows;
      this.totalItemsNormal = this.readNonNegativeNumber(
        normalized.metadata?.totalDataAssets,
        normalized.rows.length,
      );

      // The backend is the source of truth for page count. Do not derive it from
      // the current page's row count or from the frontend page-size setting.
      this.totalPagesNormal = this.readNonNegativeNumber(normalized.metadata?.totalPages, 0);
      this.cachedSource = null;

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesNormal) {
        this.currentPage = this.totalPagesNormal;
        await this.fetchDataAssetsWithMappingStatus();
        return;
      }
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error fetching data assets with mapping status:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.cachedSource = null;
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
        this.isSearching = false;
        this.refreshView();
      }
    }
  }

  async searchDataAssets(): Promise<void> {
    const query = this.searchText.trim();
    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    await this.fetchDataAssetsWithMappingStatusSearch();
  }

  async fetchDataAssetsWithMappingStatusSearch(): Promise<void> {
    if (!this.searchText.trim()) {
      await this.clearSearch();
      return;
    }

    const requestId = ++this.requestSequence;
    this.isSearching = true;
    this.isLoading = false;
    this.searchError = false;

    try {
      const apiResponse: unknown = await this.dataAssetMappingService
        .FetchDataAssetsWithMappingStatusSearch(
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader,
        )
        .toPromise();

      if (requestId !== this.requestSequence) {
        return;
      }

      const normalized = this.normalizeResponse(apiResponse);
      // The search endpoint already filters the data server-side. Keep all rows
      // returned for the requested page; do not filter them a second time here.
      this.searchedData = normalized.rows;
      this.totalItemsSearch = this.readNonNegativeNumber(
        normalized.metadata?.totalDataAssets,
        normalized.rows.length,
      );
      this.totalPagesSearch = this.readNonNegativeNumber(normalized.metadata?.totalPages, 0);
      this.cachedSource = null;

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        await this.fetchDataAssetsWithMappingStatusSearch();
        return;
      }
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error searching data assets with mapping status:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.cachedSource = null;
    } finally {
      if (requestId === this.requestSequence) {
        this.isSearching = false;
        this.isLoading = false;
        this.refreshView();
      }
    }
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.searchedData = [];
    this.cachedSource = null;
    await this.fetchDataAssetsWithMappingStatus();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.searchError = false;

    if (this.searchText.trim()) {
      this.issearch = true;
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      void this.fetchDataAssetsWithMappingStatusSearch();
    } else {
      this.issearch = false;
      void this.fetchDataAssetsWithMappingStatus();
    }
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) {
      void this.fetchDataAssetsWithMappingStatusSearch();
    } else {
      void this.fetchDataAssetsWithMappingStatus();
    }
  }

  retryLoad(): void {
    if (this.issearch && this.searchText.trim()) {
      void this.fetchDataAssetsWithMappingStatusSearch();
    } else {
      void this.fetchDataAssetsWithMappingStatus();
    }
  }

  private normalizeResponse(raw: unknown): NormalizedMappingResponse {
    if (Array.isArray(raw)) {
      const rows = raw
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        .map((item) => this.normalizeRow(item));

      return {
        rows,
        metadata: this.findMetadata(raw),
      };
    }

    if (raw && typeof raw === 'object') {
      const wrapper = raw as Record<string, unknown>;
      for (const key of ['data', 'items', 'content', 'results', 'records', 'dataAssets']) {
        if (Array.isArray(wrapper[key])) {
          const rawRows = wrapper[key] as unknown[];
          const rows = rawRows
            .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
            .map((item) => this.normalizeRow(item));
          return {
            rows,
            metadata:
              this.readMetadata(wrapper['metadata'] ?? wrapper['meta']) ??
              this.readMetadata(wrapper) ??
              this.findMetadata(rawRows),
          };
        }
      }
    }

    console.warn('[DataAssetMapping] Unrecognized API response structure:', raw);
    return { rows: [] };
  }

  private normalizeRow(item: Record<string, unknown>): DataProtection {
    const row: Record<string, unknown> = { ...item };

    // The sample response contains one record where the mapping status is under
    // an empty property name ("": "MAPPED"). Use it as a compatibility fallback.
    row['mappingStatus'] = this.normalizeMappingStatus(
      row['mappingStatus'] ?? row[''] ?? row['mapping_status'],
    );

    return row as unknown as DataProtection;
  }

  private normalizeMappingStatus(value: unknown): string {
    const status = String(value ?? '').trim().toUpperCase();
    if (status === 'MAPPED') {
      return 'MAPPED';
    }
    if (status === 'UN-MAPPED' || status === 'UNMAPPED' || status === 'NOT MAPPED') {
      return 'UN-MAPPED';
    }
    return status || 'UN-MAPPED';
  }

  private findMetadata(rows: unknown[]): MappingPaginationMeta | undefined {
    const item = rows.find((candidate) => {
      if (!candidate || typeof candidate !== 'object') {
        return false;
      }
      const row = candidate as Record<string, unknown>;
      return row['totalDataAssets'] !== undefined ||
        row['totalPages'] !== undefined ||
        row['currentPage'] !== undefined;
    });

    return item ? this.readMetadata(item) : undefined;
  }

  private readMetadata(value: unknown): MappingPaginationMeta | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const record = value as Record<string, unknown>;
    const metadata: MappingPaginationMeta = {};

    if (record['totalDataAssets'] !== undefined) {
      metadata.totalDataAssets = this.readNonNegativeNumber(record['totalDataAssets'], 0);
    }
    if (record['totalPages'] !== undefined) {
      metadata.totalPages = this.readNonNegativeNumber(record['totalPages'], 0);
    }
    if (record['currentPage'] !== undefined) {
      metadata.currentPage = this.readNonNegativeNumber(record['currentPage'], 0);
    }

    return Object.keys(metadata).length ? metadata : undefined;
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  handleAction(event: {
  action: string;
  rowIndex: number;
  rowData: DataProtection;
}): void {
  // Handle only the View action.
  if (event.action?.toLowerCase() === 'view') {
    const dataAssetId = String(event.rowData?.dataAssetId ?? '').trim();

  if (!dataAssetId || dataAssetId === '—') {
    console.error(
      '[DataAssetMapping] Cannot open View Mapping: dataAssetId is missing.',
      event.rowData,
    );
    return;
  }

  this.dialog.open(ViewMappingComponent, {
    width: '1100px',
    maxWidth: '96vw',
    maxHeight: '92vh',
    autoFocus: false,
    restoreFocus: true,
    data: {
      dataAssetId,
      dataAssetName: event.rowData.dataAssetName ?? '',
    },
  });
  }

  else if(event.action?.toLowerCase() === 'map'){
    this.router.navigate(["data-protection/auth-tools/data-asset-mapping/create",event.rowData.dataAssetId])
  }

    else if(event.action?.toLowerCase() === 'update'){
    this.router.navigate(["data-protection/auth-tools/data-asset-mapping/update",event.rowData.dataAssetId])
  }

  return;
  
}

  private refreshView(): void {
    this.changeDetector.detectChanges();
  }
}
